import * as amplitude from '@amplitude/analytics-browser';
import type { IAnalyticsAdapter, AnalyticsConfig } from '../types';

export class AmplitudeAdapter implements IAnalyticsAdapter {
  private ready = false;

  init(config: AnalyticsConfig) {
    if (this.ready) return;
    if (typeof window === 'undefined' || !config.apiKey) return;

    amplitude.init(config.apiKey, {
      autocapture: {
        attribution: true,
        pageViews: false, // we track page views ourselves for full control
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
      },
      defaultTracking: false,
    });

    this.ready = true;

    // Pin to a shared identity if configured, otherwise wait for identify()
    if (config.sharedUserId) {
      amplitude.setUserId(config.sharedUserId);
    } else if (config.userId) {
      amplitude.setUserId(config.userId);
    }
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    if (!this.ready) return;
    amplitude.setUserId(userId);

    if (traits) {
      const identifyEvent = new amplitude.Identify();
      Object.entries(traits).forEach(([key, value]) => {
        identifyEvent.set(key, value as string | number | boolean);
      });
      amplitude.identify(identifyEvent);
    }
  }

  track(event: string, properties?: Record<string, unknown>) {
    if (!this.ready) return;
    amplitude.track(event, properties);
  }

  page(pageName: string, properties?: Record<string, unknown>) {
    if (!this.ready) return;
    amplitude.track('page_viewed', { page: pageName, ...properties });
  }

  reset() {
    if (!this.ready) return;
    amplitude.reset();
  }
}
